import { FC, useEffect, useState } from 'react';

import { useParams } from 'react-router-dom';

import { Stack } from '@mui/material';
import usePricing from 'api/usePricing';
import usePublisher from 'api/usePublisher';
import useUsers from 'api/useUsers';
import { EButtonColor, EFeatureFlag, ESetting } from 'constants/enums';
import { permissionsUtil } from 'utils/permissionsUtil';

import DialogModal from 'components/Dialog/Dialog';

import { EToasterStatus, showToast } from '../../utils/showToast';

import ExchangeRateSchedulerModal from './ExchangeRateSchedulerModal';
import ExchangeRatesUpdaterManager from './ExchangeRatesUpdaterManager';
import ExchangeRateThreshold from './ExchangeRateThreshold';
import LocalizedCurrencies from './LocalizedCurrencies';
import PriceSettingToggle from './PriceSettingToggle';

import './style.scss';

export const PricingSettings: FC = () => {
  const { publisherId } = useParams();
  const { getPublisherSettings, updatePublisherSettings } = usePublisher(
    publisherId ? publisherId : undefined
  );
  const { fetchFeatureFlags } = useUsers({ publisherId: publisherId });
  const { updateExchangeRates } = usePricing(publisherId);
  const [updateExchangeRatesDialogOpen, setUpdateExchangeRatesDialogOpen] =
    useState(false);
  const [
    isExchangeRateSchedulerModalOpen,
    setIsExchangeRateSchedulerModalOpen
  ] = useState(false);
  const [isDialogOpen, setDialogOpen] = useState(false);
  const [dialogType, setDialogType] = useState<ESetting | null>(null);
  const [settings, setSettings] = useState({
    roundingRulesEnabled: false,
    taxEnabled: false
  });
  const isSuperAdmin = permissionsUtil.isSuperAdminByProject(publisherId ?? '');

  const hasFeatureFlagExchangeRateUpdateScheduler =
    fetchFeatureFlags.data?.featureFlags?.[
      EFeatureFlag.DASHBOARD_EXCHANGE_RATE_UPDATE_SCHEDULER
    ];
  const hasFeatureFlagPricePointAlerting =
    fetchFeatureFlags.data?.featureFlags?.[
      EFeatureFlag.DASHBOARD_PRICE_POINT_ALERTING
    ];
  const hasFeatureFlagDisableCurrencies =
    fetchFeatureFlags.data?.featureFlags?.[
      EFeatureFlag.DASHBOARD_DISABLE_CURRENCIES
    ];

  useEffect(() => {
    getPublisherSettings
      .refetch()
      .then((response) => {
        const settings = response.data;
        setSettings({
          roundingRulesEnabled: settings?.roundingOn,
          taxEnabled: settings?.taxOn
        });
      })
      .catch((error) => {
        showToast({
          message: 'Failed to load settings',
          status: EToasterStatus.ERROR
        });
        console.error('Error fetching publisher settings:', error);
      });
  }, []);

  const handleSettingChange = (settingType: ESetting) => {
    setDialogType(settingType);
    setDialogOpen(true);
  };

  const submitSettingChange = () => {
    if (!dialogType) return;
    const settingName =
      dialogType === ESetting.ROUNDING_ON ? 'Rounding rules' : 'Tax';
    const isActive =
      dialogType === ESetting.ROUNDING_ON
        ? !settings.roundingRulesEnabled
        : !settings.taxEnabled;
    updatePublisherSettings.mutate(
      { setting: dialogType, isActive },
      {
        onSuccess: () => {
          showToast({
            message: `${settingName} setting updated successfully`,
            status: EToasterStatus.SUCCESS
          });
          setSettings((prevSettings) => ({
            ...prevSettings,
            [dialogType === ESetting.ROUNDING_ON
              ? 'roundingRulesEnabled'
              : 'taxEnabled']: isActive
          }));
          setDialogOpen(false);
        },
        onError: () => {
          showToast({
            message: `Error updating ${settingName} setting`,
            status: EToasterStatus.ERROR
          });
          setDialogOpen(false);
        }
      }
    );
  };

  const updateExchangeRatesHandler = async () => {
    try {
      await updateExchangeRates.mutateAsync();
      showToast({
        message: 'Exchange rates updated successfully',
        status: EToasterStatus.SUCCESS
      });
    } catch (error) {
      showToast({
        message: 'Error updating exchange rates',
        status: EToasterStatus.ERROR
      });
    }
    setUpdateExchangeRatesDialogOpen(false);
  };

  return (
    <Stack maxWidth={'auto'} marginBottom={10}>
      {hasFeatureFlagDisableCurrencies && <LocalizedCurrencies />}
      <ExchangeRatesUpdaterManager
        setUpdateExchangeRatesDialogOpen={setUpdateExchangeRatesDialogOpen}
        setIsExchangeRateSchedulerModalOpen={
          setIsExchangeRateSchedulerModalOpen
        }
        isSchedulerEnabled={hasFeatureFlagExchangeRateUpdateScheduler}
      />
      <PriceSettingToggle
        title="Display Tax-Inclusive Prices"
        description="Automatically include tax in local prices for countries where it’s required by law to show tax-inclusive pricing."
        action={() => handleSettingChange(ESetting.TAX_ON)}
        isOn={settings.taxEnabled}
        disabled={
          getPublisherSettings.isLoading ||
          getPublisherSettings.isFetching ||
          !isSuperAdmin
        }
      />
      <PriceSettingToggle
        title="Rounding Rules"
        description="Automatically round localized prices to .00, .09, .90 or .99."
        action={() => handleSettingChange(ESetting.ROUNDING_ON)}
        isOn={settings.roundingRulesEnabled}
        disabled={
          getPublisherSettings.isLoading ||
          getPublisherSettings.isFetching ||
          !isSuperAdmin
        }
      />
      {hasFeatureFlagPricePointAlerting && <ExchangeRateThreshold />}
      <DialogModal
        isOpen={isDialogOpen}
        headline={'Changes Approval'}
        text={
          'Enabling this toggle will affect all existing price points. Do you approve?'
        }
        buttons={[
          {
            text: 'Cancel',
            color: EButtonColor.SECONDARY,
            variant: 'outlined',
            func: () => {
              setDialogOpen(false);
            }
          },
          {
            text: 'Approve',
            color: EButtonColor.PRIMARY,
            variant: 'contained',
            func: submitSettingChange
          }
        ]}
        closeDialog={() => setDialogOpen(false)}
      />
      <ExchangeRateSchedulerModal
        isOpen={isExchangeRateSchedulerModalOpen}
        setIsExchangeRateSchedulerModalOpen={
          setIsExchangeRateSchedulerModalOpen
        }
      />
      <DialogModal
        isOpen={updateExchangeRatesDialogOpen}
        closeButton={false}
        headline="Update Exchange Rates"
        width={'392px'}
        text={
          'All existing price points will be updated according to the latest exchange rate. Players will automatically see the new prices at the store'
        }
        buttons={[
          {
            text: 'Cancel',
            color: EButtonColor.SECONDARY,
            variant: 'outlined',
            func: () => setUpdateExchangeRatesDialogOpen(false),
            disabled: false,
            fullWidth: false
          },
          {
            text: 'Update',
            color: EButtonColor.PRIMARY,
            variant: 'contained',
            func: () => updateExchangeRatesHandler(),
            disabled: false,
            fullWidth: false
          }
        ]}
      />
    </Stack>
  );
};

export default PricingSettings;
